// src/pages/RegisterPage.jsx
// Signup for both roles. Split layout: brand panel left, form right, collapsing
// to form-only on mobile.
//
// Referral attribution happens here and only here. The code is validated live
// as they type so they can see who they are being credited to before they
// submit. A bad code never blocks registration - they can continue without one.

import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import {
  Mail, Lock, ArrowRight, Receipt, Scan, BarChart3, Ticket,
  ClipboardList, Link2, Building2, Users, BadgeCheck,
} from "../components/icons";
import PasswordEye from "../components/PasswordEye";
import { validateReferral } from "../api/auth";
import { useAuth } from "../context/AuthContext";
import { homePathForRole } from "../config";
import ThemeToggle from "../components/ThemeToggle";

// ambient floating glyphs on the brand panel
const FLOATERS = [
  { Icon: Receipt, top: "14%", left: "16%", size: 30, dur: "7s", delay: "0s" },
  { Icon: Scan, top: "26%", right: "18%", size: 26, dur: "8s", delay: "1s" },
  { Icon: BarChart3, bottom: "22%", left: "12%", size: 28, dur: "9s", delay: "2s" },
  { Icon: Ticket, bottom: "34%", right: "14%", size: 24, dur: "7.5s", delay: "1.5s" },
  { Icon: ClipboardList, top: "48%", left: "8%", size: 22, dur: "8.5s", delay: ".5s" },
  { Icon: Link2, top: "62%", right: "10%", size: 22, dur: "6.5s", delay: "2.5s" },
  { Icon: Building2, top: "10%", right: "40%", size: 20, dur: "9.5s", delay: "3s" },
  { Icon: Lock, bottom: "12%", right: "42%", size: 20, dur: "8s", delay: "1.2s" },
];

const POINTS = [
  { t: "Scan once, sorted forever", d: "Every line item read and filed under the right SARS category." },
  { t: "Proof behind every claim", d: "The original slip image stays attached to the deduction." },
  { t: "Hand it to your practitioner", d: "Release your year and they get the whole pack, read-only." },
];

const INPUT =
  "w-full rounded-lg border border-tb-border dark:border-tb-d-border bg-white dark:bg-tb-d-inset " +
  "px-3 py-2.5 text-sm outline-none focus:border-tb-brand focus:ring-1 focus:ring-tb-brand " +
  "placeholder:text-tb-muted/60";

const LABEL = "block text-sm font-medium mb-1.5";

export default function RegisterPage() {
  const { user, register } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [isPrac, setIsPrac] = useState(false);

  // referral
  const [hasCode, setHasCode] = useState(null);   // null = not answered yet
  const [code, setCode] = useState("");
  const [check, setCheck] = useState(null);       // { valid, partner?, consultant? }
  const [checking, setChecking] = useState(false);

  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const timer = useRef(null);

  // debounced live validation. never blocks anything, just shows who they get
  // credited to so they can correct a typo before submitting.
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const c = code.trim();
    if (c.length < 3) { setCheck(null); setChecking(false); return; }

    setChecking(true);
    timer.current = setTimeout(async () => {
      try {
        const r = await validateReferral(c);
        setCheck(r);
      } catch {
        setCheck(null);
      } finally {
        setChecking(false);
      }
    }, 450);

    return () => timer.current && clearTimeout(timer.current);
  }, [code]);

  // already signed in -> go home
  if (user) return <Navigate to={homePathForRole(user.role)} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (hasCode === null) {
      setError("Please tell us whether you have a referral code.");
      return;
    }
    if (hasCode && !code.trim()) {
      setError("Enter your referral code, or choose \u201cNo referral code\u201d.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      const profile = await register({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim(),
        password,
        role: isPrac ? "practitioner" : "user",
        referral_code: hasCode ? code.trim() : "",
      });
      navigate(homePathForRole(profile.role), { replace: true });
    } catch (err) {
      const map = {
        EMAIL_EXISTS: "That email is already registered.",
        PHONE_EXISTS: "That phone number is already registered.",
        INVALID_PHONE: "Please enter a valid phone number.",
        REFERRAL_CODE_INVALID:
          "We could not find that referral code. Check it with whoever referred you, or continue without one.",
      };
      setError(map[err.code] || err.message || "Could not create your account.");
    } finally {
      setBusy(false);
    }
  };

  const codeState = checking ? "checking" : check?.valid ? "valid" : code.trim().length >= 3 ? "unknown" : "idle";

  return (
    <div className="min-h-screen flex bg-tb-bg dark:bg-tb-night text-tb-ink dark:text-tb-d-text">

      {/* ---------- brand panel (desktop only) ---------- */}
      <aside className="hidden lg:flex lg:w-[46%] xl:w-[42%] relative overflow-hidden bg-tb-night text-white flex-col justify-between p-12 xl:p-16">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-32 w-[36rem] h-[36rem] rounded-full bg-tb-brand/20 blur-3xl" />
          <div className="absolute -bottom-48 -left-32 w-[32rem] h-[32rem] rounded-full bg-tb-cta/15 blur-3xl" />
          {FLOATERS.map(({ Icon, size, top, bottom, left, right, dur, delay }, i) => (
            <div
              key={i}
              className="absolute tb-float text-tb-brand opacity-[0.13]"
              style={{ top, bottom, left, right, animationDuration: dur, animationDelay: delay }}
            >
              <Icon size={size} />
            </div>
          ))}
        </div>

        <div className="relative z-10">
          <img src={process.env.PUBLIC_URL + "/logo-dark.png"} alt="taxpack" className="h-11 w-auto" />
        </div>

        <div className="relative z-10 max-w-md">
          <h2 className="text-4xl xl:text-[2.75rem] font-display font-bold leading-[1.08] mb-4">
            Every slip.<br />
            <span className="text-tb-brand">Sorted automatically.</span>
          </h2>
          <p className="text-white/55 leading-relaxed mb-10">
            Photograph a receipt and taxpack does the rest &mdash; reading it, categorising it, and
            keeping the proof for tax season.
          </p>

          <ul className="space-y-5">
            {POINTS.map((p) => (
              <li key={p.t} className="flex gap-3.5">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-tb-brand shrink-0" />
                <div>
                  <p className="font-semibold text-[15px] leading-snug">{p.t}</p>
                  <p className="text-sm text-white/45 leading-relaxed mt-0.5">{p.d}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative z-10 flex items-center gap-4 text-xs text-white/35">
          <span>Built in South Africa</span>
          <span className="h-1 w-1 rounded-full bg-white/25" />
          <span>Five free scans to try it</span>
        </div>
      </aside>

      {/* ---------- form ---------- */}
      <main className="flex-1 flex flex-col relative">
        <div className="flex justify-end px-6 pt-5">
          <ThemeToggle />
        </div>

        <div className="flex-1 flex items-center justify-center px-5 pb-12 pt-2">
          <div className="w-full max-w-lg">

            {/* logo, mobile only */}
            <div className="lg:hidden flex justify-center mb-7">
              <img src={process.env.PUBLIC_URL + "/logo-light.png"} alt="taxpack" className="h-11 w-auto dark:hidden" />
              <img src={process.env.PUBLIC_URL + "/logo-dark.png"} alt="taxpack" className="h-11 w-auto hidden dark:block" />
            </div>

            <h1 className="text-3xl font-display font-bold mb-1.5 lg:text-left text-center">
              Create your account
            </h1>
            <p className="text-sm text-tb-muted mb-7 lg:text-left text-center">
              {isPrac
                ? "Free for tax practitioners. Takes about a minute."
                : "Track your SARS deductions from every slip."}
            </p>

            <form onSubmit={onSubmit} className="surface rounded-2xl border border-tb-border dark:border-tb-d-border p-6 sm:p-7 shadow-sm">
              {error && (
                <div className="mb-5 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 px-3 py-2.5 text-sm text-red-700 dark:text-red-300">
                  {error}
                </div>
              )}

              {/* name + phone */}
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={LABEL} htmlFor="name">Full name</label>
                  <input
                    id="name" type="text" autoComplete="name" required
                    value={name} onChange={(e) => setName(e.target.value)}
                    className={INPUT} placeholder="Jane Dlamini"
                  />
                </div>
                <div>
                  <label className={LABEL} htmlFor="phone">Mobile number</label>
                  <input
                    id="phone" type="tel" autoComplete="tel" required
                    value={phone} onChange={(e) => setPhone(e.target.value)}
                    className={INPUT} placeholder="082 123 4567"
                  />
                </div>
              </div>

              {/* email */}
              <label className={LABEL} htmlFor="email">Email</label>
              <div className="relative mb-4">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-tb-muted" />
                <input
                  id="email" type="email" autoComplete="email" required
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className={INPUT + " pl-9"} placeholder="you@company.co.za"
                />
              </div>

              {/* password */}
              <label className={LABEL} htmlFor="password">Password</label>
              <div className="relative mb-1.5">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-tb-muted" />
                <input
                  id="password" type={showPw ? "text" : "password"} autoComplete="new-password" required
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className={INPUT + " pl-9 pr-12"} placeholder="At least 8 characters"
                />
                <PasswordEye visible={showPw} onToggle={() => setShowPw((v) => !v)} />
              </div>
              <p className="mb-6 text-xs text-tb-muted">Minimum 8 characters.</p>

              {/* practitioner toggle */}
              <button
                type="button"
                onClick={() => setIsPrac((v) => !v)}
                className={`w-full flex items-start gap-3 rounded-xl border px-4 py-3.5 text-left transition mb-6 ${
                  isPrac
                    ? "border-tb-brand bg-tb-brand/[0.07]"
                    : "border-tb-border dark:border-tb-d-border hover:border-tb-brand/50"
                }`}
              >
                <span className={`mt-0.5 grid place-items-center h-5 w-5 rounded-md border shrink-0 transition ${
                  isPrac ? "border-tb-brand bg-tb-brand text-white" : "border-tb-border dark:border-tb-d-border"
                }`}>
                  {isPrac && <span className="text-[11px] leading-none font-bold">&#10003;</span>}
                </span>
                <span className="min-w-0">
                  <span className="flex items-center gap-2 text-sm font-medium">
                    <Users size={15} className={isPrac ? "text-tb-brand" : "text-tb-muted"} />
                    I am a tax practitioner
                  </span>
                  <span className="block text-xs text-tb-muted leading-relaxed mt-1">
                    Free account with read-only access to clients who share a connect code with you.
                  </span>
                </span>
              </button>

              {/* referral - two options, required */}
              <fieldset className="mb-6">
                <legend className={LABEL}>Were you referred to taxpack?</legend>

                <div className="grid grid-cols-2 gap-2 mb-3">
                  {[
                    { v: true, label: "I have a referral code" },
                    { v: false, label: "No referral code" },
                  ].map((o) => {
                    const on = hasCode === o.v;
                    return (
                      <label
                        key={String(o.v)}
                        className={`flex items-center gap-2.5 rounded-xl border px-3.5 py-3 cursor-pointer transition ${
                          on
                            ? "border-tb-brand bg-tb-brand/[0.07]"
                            : "border-tb-border dark:border-tb-d-border hover:border-tb-brand/50"
                        }`}
                      >
                        <input
                          type="radio"
                          name="hasCode"
                          checked={on}
                          onChange={() => { setHasCode(o.v); if (!o.v) { setCode(""); setCheck(null); } }}
                          className="sr-only"
                        />
                        <span className={`grid place-items-center h-[18px] w-[18px] rounded-full border-2 shrink-0 transition ${
                          on ? "border-tb-brand" : "border-tb-border dark:border-tb-d-border"
                        }`}>
                          {on && <span className="h-2 w-2 rounded-full bg-tb-brand" />}
                        </span>
                        <span className={`text-xs font-medium ${on ? "text-tb-brand" : ""}`}>{o.label}</span>
                      </label>
                    );
                  })}
                </div>

                {hasCode === true && (
                  <>
                    <input
                      id="referral"
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase().replace(/\s/g, ""))}
                      className={INPUT + " font-mono tracking-widest"}
                                            placeholder="Enter your code"
                      autoComplete="off"
                      autoFocus
                    />

                    {/* live result */}
                    <div className="mt-2 min-h-[1.25rem]">
                      {codeState === "checking" && (
                        <p className="text-xs text-tb-muted">Checking...</p>
                      )}
                      {codeState === "valid" && (
                        <p className="text-xs text-tb-brand font-medium flex items-center gap-1.5">
                          <BadgeCheck size={14} />
                          {check.partner}
                          {check.consultant ? ` \u2014 ${check.consultant}` : ""}
                        </p>
                      )}
                      {codeState === "unknown" && (
                        <p className="text-xs text-tb-muted">
                          We do not recognise that code yet. Check it with whoever referred you.
                        </p>
                      )}
                    </div>
                  </>
                )}
              </fieldset>

              {/* submit */}
              <button
                type="submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-tb-cta
                           text-white text-sm font-semibold py-3 shadow-green-glow
                           hover:brightness-105 active:scale-[0.99] disabled:opacity-60 transition"
              >
                {busy ? "Creating account..." : "Create account"}
                {!busy && <ArrowRight size={16} />}
              </button>

              <p className="mt-5 text-center text-sm text-tb-muted">
                Already have an account?{" "}
                <Link to="/login" className="text-tb-brand font-semibold hover:underline">Sign in</Link>
              </p>
            </form>

            {!isPrac && (
              <p className="mt-5 text-center text-xs text-tb-muted leading-relaxed">
                Scanning receipts happens in the taxpack mobile app. Sign up here, then download the
                app to start capturing slips.
              </p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}